(() => {
  if (customElements.get('yamsuph-story')) return;

  const INTRO_DURATION = 18;
  const CARD_DURATION = 3.2;
  const CARD_HANDOFF = INTRO_DURATION + CARD_DURATION;
  const HANDOFF_FADE = .6;
  const DURATION = 25;
  const FRAME_INTERVAL = 1000 / 24;
  const COMPASS_FRAMES = [[0, 0], [3.8, 0], [5.5, 22], [7.2, -31], [9.5, 48], [11.5, -18], [13.5, 8], [14.6, 5], [15.8, -4], [17, 0]];
  const clamp = (value) => Math.min(1, Math.max(0, value));
  const ease = (value) => value * value * value * (value * (value * 6 - 15) + 10);

  function compassAngle(time) {
    for (let index = 1; index < COMPASS_FRAMES.length; index += 1) {
      const [end, to] = COMPASS_FRAMES[index];
      if (time > end) continue;
      const [start, from] = COMPASS_FRAMES[index - 1];
      return from + (to - from) * ease(clamp((time - start) / (end - start)));
    }
    return 0;
  }

  class YamsuphStory extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.controller = new AbortController();
      this.elapsed = 0;
      this.timelineComplete = false;
      this.dataset.introComplete = 'false';
      this.frame = 0;
      this.lastClock = null;
      this.lastDraw = null;
      this.inViewport = true;
      this.needsResize = true;
      this.renderer = null;
      this.frozenAnimations = new Set();
      this.backdrop = document.body.classList.contains('yamsuph-immersive')
        ? document.querySelector('[data-yamsuph-backdrop]') : null;
      this.immersive = Boolean(this.backdrop);
      this.canvas = (this.backdrop || this).querySelector('canvas[data-blood-sea]');
      this.ending = this.querySelector('[data-ending]');
      this.cardBridge = this.querySelector('[data-card-bridge]');
      delete this.dataset.cardHandoff;
      this.skipButton = this.querySelector('[data-skip-intro]');
      this.pauseButtons = Array.from(this.querySelectorAll('button[data-pause]'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.dataset.enhanced = 'true';
      this.classList.add('is-replaying');
      this.classList.remove('is-ready');
      this.tick = (now) => this.advance(now);

      const signal = this.controller.signal;
      if (this.hasAttribute('data-oracle')) {
        document.body.classList.add('ys-oracle-active');
        this.addEventListener('yamsuph:reading', () => document.body.classList.add('ys-oracle-has-reading'), { signal });
        this.addEventListener('yamsuph:circle', () => document.body.classList.remove('ys-oracle-has-reading'), { signal });
      }
      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.reducedMotion.addEventListener('change', () => this.updatePlayback(), { signal });
      window.addEventListener('resize', () => this.handleResize(), { passive: true, signal });
      if (this.immersive) window.addEventListener('scroll', () => this.handlePageScroll(), { passive: true, signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this || event.target.contains?.(this)) this.teardown();
      }, { signal });
      this.canvas?.addEventListener('webglcontextlost', () => this.useCSSRenderer(), { signal });
      this.overlayObserver = new MutationObserver(() => this.updatePlayback());
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          if (!entry || this.inViewport === entry.isIntersecting) return;
          this.inViewport = entry.isIntersecting;
          this.updatePlayback();
        }, { threshold: 0 });
        this.visibilityObserver.observe(this);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this);
      }

      this.createRenderer();
      this.updatePlayback();
      this.handlePageScroll();
    }

    disconnectedCallback() {
      this.teardown();
    }

    createRenderer() {
      this.dataset.renderer = 'css';
      if (this.backdrop) this.backdrop.dataset.renderer = 'css';
      if (!this.canvas || typeof window.YamSuphBloodSea !== 'function') return;
      try {
        this.renderer = new window.YamSuphBloodSea(this.canvas);
        if (!this.renderer?.available) {
          this.useCSSRenderer();
          return;
        }
        this.dataset.renderer = 'webgl';
        if (this.backdrop) this.backdrop.dataset.renderer = 'webgl';
      } catch {
        this.useCSSRenderer();
      }
    }

    useCSSRenderer() {
      const renderer = this.renderer;
      this.renderer = null;
      this.dataset.renderer = 'css';
      if (this.backdrop) this.backdrop.dataset.renderer = 'css';
      try { renderer?.destroy(); } catch { /* The CSS sea remains available after context loss. */ }
    }

    canRun() {
      return this.connected && (this.immersive || this.inViewport) && !document.hidden
        && !this.userPaused && !this.reducedMotion.matches
        && !document.body.classList.contains('pw-aurora-is-open');
    }

    updatePlayback() {
      if (!this.connected) return;
      if (this.reducedMotion.matches) {
        this.elapsed = DURATION;
        this.releaseAnimations(true);
      }
      const running = this.canRun();
      this.dataset.motion = running ? 'running' : 'paused';
      if (this.backdrop) this.backdrop.dataset.motion = this.dataset.motion;
      this.dataset.reducedMotion = String(this.reducedMotion.matches);
      this.pauseButtons.forEach((button) => {
        const paused = Boolean(this.userPaused || this.reducedMotion.matches);
        let label = button.dataset.labelPause || 'Pause motion';
        if (this.reducedMotion.matches) label = button.dataset.labelReduced || 'Reduced motion';
        else if (this.userPaused) label = button.dataset.labelResume || 'Resume motion';
        button.setAttribute('aria-label', label);
        button.setAttribute('aria-pressed', String(paused));
        button.disabled = this.reducedMotion.matches;
      });
      if (!running) this.stopFrame();
      this.renderScene();
      if (running) {
        this.releaseAnimations();
        this.requestFrame();
      } else {
        this.freezeAnimations();
      }
    }

    freezeAnimations() {
      for (const animation of this.getAnimations?.({ subtree: true }) || []) {
        if (animation.playState === 'finished' || animation.playState === 'idle') continue;
        try {
          animation.pause();
          this.frozenAnimations.add(animation);
        } catch { /* A removed CSS transition needs no further suspension. */ }
      }
    }

    releaseAnimations(cancel = false) {
      for (const animation of this.frozenAnimations) {
        try {
          if (cancel) animation.cancel();
          else if (animation.playState !== 'finished' && animation.playState !== 'idle') animation.play();
        } catch { /* Replay or a media query may already have removed this effect. */ }
      }
      this.frozenAnimations.clear();
    }

    requestFrame() {
      if (this.frame || !this.canRun()) return;
      // The final sea drifts as a compositor layer; no ongoing shader or DOM work.
      if (this.timelineComplete) return;
      this.frame = requestAnimationFrame(this.tick);
    }

    stopFrame() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastClock = null;
      this.lastDraw = null;
    }

    advance(now) {
      this.frame = 0;
      if (!this.canRun()) {
        this.lastClock = null;
        this.lastDraw = null;
        return;
      }
      if (this.lastClock !== null) this.elapsed = Math.min(DURATION, this.elapsed + Math.max(0, now - this.lastClock) / 1000);
      this.lastClock = now;
      const reachedFinal = this.elapsed >= DURATION && !this.timelineComplete;
      if (this.lastDraw === null || now - this.lastDraw >= FRAME_INTERVAL || reachedFinal) {
        this.lastDraw = now;
        this.renderScene();
      }
      this.requestFrame();
    }

    renderScene() {
      const time = this.elapsed;
      const reduced = this.reducedMotion.matches;
      const bloodProgress = ease(clamp((time - 5.2) / (13.5 - 5.2)));
      const silverProgress = ease(clamp((time - 13) / 3));
      const fast = this.dataset.fastEntry === 'true';
      const bridge = Boolean(this.cardBridge && this.hasAttribute('data-oracle'));
      const entryTime = bridge ? CARD_HANDOFF : INTRO_DURATION;
      const cardProgress = reduced || fast ? 1 : ease(clamp((time - INTRO_DURATION) / CARD_DURATION));
      const cardOpacity = bridge && !reduced && !fast
        ? ease(clamp((time - INTRO_DURATION) / .35)) * (1 - ease(clamp((time - CARD_HANDOFF) / HANDOFF_FADE))) : 0;
      const comingProgress = reduced || fast ? 1 : ease(clamp((time - entryTime) / (bridge ? HANDOFF_FADE : 2.5)));
      const cardHandoff = bridge && !reduced && !fast && time >= CARD_HANDOFF;
      const angle = compassAngle(time);
      const driftStrength = reduced ? 0 : ease(clamp((time - 3.8) / 4));
      const driftTime = Math.max(0, time - 3.8);
      let glintX = 200;
      if (reduced) glintX = 40;
      else if (time >= 14 && time <= INTRO_DURATION) glintX = 160 - 240 * ease(clamp((time - 14) / 4));
      else if (time > INTRO_DURATION) {
        // Keep the beam outside the artwork between slow five-second passes.
        // Its reset happens beyond the edge, avoiding a visible flash.
        const cycle = (time - INTRO_DURATION) % 16;
        glintX = 160 - 240 * ease(clamp((cycle - 11) / 5));
      }
      this.timelineComplete = time >= DURATION;
      if (this.backdrop) this.backdrop.dataset.settled = String(this.timelineComplete);
      this.dataset.time = time.toFixed(3);
      this.dataset.stage = time < 3.8 ? 'arrival' : time < 5.2 ? 'seeking' : time < 14 ? 'blood' : time < INTRO_DURATION ? 'silver' : bridge && time < CARD_HANDOFF ? 'card' : 'oracle';
      const introComplete = time >= entryTime;
      this.ending?.setAttribute('aria-hidden', String(!introComplete));
      if (this.ending) this.ending.inert = !introComplete;
      if (this.skipButton) this.skipButton.hidden = introComplete;
      if (cardHandoff) this.dataset.cardHandoff = 'true';
      else delete this.dataset.cardHandoff;
      this.dataset.compassAngle = angle.toFixed(3);
      this.style.setProperty('--elapsed', `${time.toFixed(3)}s`);
      this.style.setProperty('--blood-progress', bloodProgress.toFixed(5));
      this.backdrop?.style.setProperty('--blood-progress', bloodProgress.toFixed(5));
      this.style.setProperty('--coming-progress', comingProgress.toFixed(5));
      this.style.setProperty('--card-progress', cardProgress.toFixed(5));
      this.style.setProperty('--card-opacity', cardOpacity.toFixed(5));
      this.style.setProperty('--silver-progress', silverProgress.toFixed(5));
      this.style.setProperty('--compass-angle', `${angle.toFixed(3)}deg`);
      this.style.setProperty('--glint-x', `${glintX.toFixed(3)}%`);
      this.style.setProperty('--float-phase', (Math.sin(driftTime * 0.32) * driftStrength).toFixed(5));
      this.style.setProperty('--drift-x', `${(Math.sin(driftTime * 0.31) * 1.25 * driftStrength).toFixed(4)}px`);
      this.style.setProperty('--drift-y', `${(Math.sin(driftTime * 0.23 + 0.6) * 1.05 * driftStrength).toFixed(4)}px`);
      this.style.setProperty('--light-x', `${(50 + Math.sin(driftTime * 0.14) * 15 * driftStrength).toFixed(3)}%`);
      if (introComplete && this.dataset.introComplete !== 'true') {
        this.dataset.introComplete = 'true';
        this.dispatchEvent(new CustomEvent('yamsuph:intro-ready', { detail: { fast, cardHandoff } }));
      }
      if (time >= 0.5) {
        this.classList.remove('is-replaying');
        this.classList.add('is-ready');
      }
      if (!this.renderer || (!this.immersive && !this.inViewport) || document.hidden) return;
      try {
        if (this.needsResize) {
          const bounds = this.canvas.getBoundingClientRect();
          this.renderer.resize(
            Math.max(1, Math.round(bounds.width)),
            Math.max(1, Math.round(bounds.height)),
            Math.min(1, Math.sqrt(700000 / Math.max(1, bounds.width * bounds.height))),
          );
          this.needsResize = false;
        }
        if (this.renderer.render(time, bloodProgress) === false) this.useCSSRenderer();
      } catch {
        this.useCSSRenderer();
      }
    }

    handleResize() {
      this.needsResize = true;
      if (this.canRun()) this.requestFrame();
      else if ((this.immersive || this.inViewport) && !document.hidden) this.renderScene();
    }

    handlePageScroll() {
      // If someone skips the opening, keep the shop readable against the final sea.
      if (!this.immersive || this.hasAttribute('data-oracle') || this.elapsed >= DURATION) return;
      if (this.getBoundingClientRect().bottom >= window.innerHeight * 0.55) return;
      this.elapsed = DURATION;
      this.updatePlayback();
    }

    handleClick(event) {
      const target = event.target;
      if (target.closest?.('[data-skip-intro]') && this.dataset.introComplete !== 'true') {
        this.stopFrame();
        this.releaseAnimations(true);
        this.dataset.fastEntry = 'true';
        this.elapsed = DURATION;
        this.userPaused = false;
        this.updatePlayback();
        return;
      }
      const pause = target.closest?.('button[data-pause]');
      if (pause && this.contains(pause)) {
        if (!this.reducedMotion.matches) {
          this.userPaused = !this.userPaused;
          this.updatePlayback();
        }
        return;
      }
      const replay = target.closest?.('button[data-replay]');
      if (!replay || !this.contains(replay)) return;
      this.stopFrame();
      this.releaseAnimations(true);
      this.elapsed = this.reducedMotion.matches ? DURATION : 0;
      this.timelineComplete = false;
      this.dataset.introComplete = 'false';
      delete this.dataset.cardHandoff;
      delete this.dataset.fastEntry;
      document.body.classList.remove('ys-oracle-has-reading');
      this.dispatchEvent(new CustomEvent('yamsuph:intro-reset'));
      if (this.hasAttribute('data-oracle')) this.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.classList.add('is-replaying');
      this.classList.remove('is-ready');
      this.needsResize = true;
      // Replay restarts the scene. Under a system motion preference it presents
      // the finished still; otherwise this explicit action resumes playback.
      this.userPaused = false;
      this.updatePlayback();
    }

    teardown() {
      if (!this.connected) return;
      this.connected = false;
      this.stopFrame();
      this.releaseAnimations(true);
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.overlayObserver?.disconnect();
      if (this.hasAttribute('data-oracle')) document.body.classList.remove('ys-oracle-active', 'ys-oracle-has-reading');
      this.useCSSRenderer();
    }
  }

  customElements.define('yamsuph-story', YamsuphStory);
})();
